import React, { useState, useEffect, useRef } from "react";
import Header from "../../components/content_wrapper/header";
import { useGetTransactionLogs } from "../../hooks/transactionManagementApi/getLogs";
import Pagination from "../../utils/pagination";
import moment from "moment";
import getSymbolFromCurrency from "currency-symbol-map";
import CustomLaoder from "../../components/customLoader";
import NoRecordFound from "../../components/noRecordFound";

// ─── Status badge ─────────────────────────────────────────────────────────────
const STATUS_COLOURS: Record<string, string> = {
  succeeded: "#27ae60",
  active:    "#27ae60",
  paid:      "#27ae60",
  failed:    "#e74c3c",
  canceled:  "#7f8c8d",
  cancelled: "#7f8c8d",
  pending:   "#f39c12",
  refunded:  "#8e44ad",
};

function StatusBadge({ status }: { status?: string }) {
  if (!status) return <span style={{ color: "#aaa" }}>—</span>;
  const colour = STATUS_COLOURS[status.toLowerCase()] || "#95a5a6";
  return (
    <span
      style={{
        background: colour,
        color: "#fff",
        padding: "2px 9px",
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 600,
        whiteSpace: "nowrap",
        textTransform: "capitalize",
      }}
    >
      {status}
    </span>
  );
}

// ─── Stat card ────────────────────────────────────────────────────────────────
function StatCard({
  icon,
  label,
  value,
  colour,
}: {
  icon: string;
  label: string;
  value: string;
  colour: string;
}) {
  return (
    <div className="col-6 col-md-3">
      <div
        className="card h-100"
        style={{ borderLeft: `4px solid ${colour}` }}
      >
        <div className="card-body py-3 px-3">
          <div className="d-flex align-items-center gap-2 mb-1">
            <i className={`${icon}`} style={{ color: colour, fontSize: 14 }} />
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: "#6c757d",
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              {label}
            </span>
          </div>
          <div
            style={{ fontSize: 20, fontWeight: 700, color: "#212529" }}
          >
            {value}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Transaction detail modal ─────────────────────────────────────────────────
function DetailModal({
  item,
  onClose,
}: {
  item: any;
  onClose: () => void;
}) {
  const sym = getSymbolFromCurrency(item?.currency) || "£";
  const money = (v: any) => `${sym}${Number(v ?? 0).toFixed(2)}`;
  return (
    <div
      className="modal show"
      style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-lg modal-dialog-scrollable"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="fa-solid fa-receipt me-2 text-primary" />
              Transaction Detail
            </h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body" style={{ fontSize: 13 }}>
            {/* User */}
            <h6
              className="fw-semibold text-muted mb-2"
              style={{
                fontSize: 11,
                textTransform: "uppercase",
                letterSpacing: 1,
              }}
            >
              User
            </h6>
            <table className="table table-bordered table-sm mb-4">
              <tbody>
                <tr>
                  <th style={{ width: 160 }}>Name</th>
                  <td>{item.name || "—"}</td>
                </tr>
                <tr>
                  <th>Email</th>
                  <td>{item.email || "—"}</td>
                </tr>
              </tbody>
            </table>

            {/* Payment */}
            <h6
              className="fw-semibold text-muted mb-2"
              style={{
                fontSize: 11,
                textTransform: "uppercase",
                letterSpacing: 1,
              }}
            >
              Payment
            </h6>
            <table className="table table-bordered table-sm mb-4">
              <tbody>
                <tr>
                  <th style={{ width: 170 }}>Type</th>
                  <td className="text-capitalize">
                    {item.type === "recurring" ? "renewal" : item.type || "—"}
                  </td>
                </tr>
                <tr>
                  <th>Plan</th>
                  <td className="text-capitalize">{item.planName || "—"}</td>
                </tr>
                <tr>
                  <th>Key</th>
                  <td>{item.keyName || "—"}</td>
                </tr>
                <tr>
                  <th>Gross</th>
                  <td>{money(item.grossAmount)}</td>
                </tr>
                <tr>
                  <th>Discount</th>
                  <td>
                    {Number(item.discountAmount) > 0 ? `-${money(item.discountAmount)}` : "—"}
                    {item.coupon ? (
                      <span style={{ marginLeft: 8, color: "#72BCB8", fontWeight: 600, fontSize: 12 }}>
                        Coupon: {item.coupon}
                      </span>
                    ) : null}
                  </td>
                </tr>
                <tr>
                  <th>Tax</th>
                  <td>{money(item.taxAmount)}</td>
                </tr>
                <tr>
                  <th>Paid</th>
                  <td><strong>{money(item.paidAmount)}</strong></td>
                </tr>
                {Number(item.refundAmount) > 0 && (
                  <tr>
                    <th>Refunded</th>
                    <td style={{ color: "#8e44ad" }}>-{money(item.refundAmount)}</td>
                  </tr>
                )}
                <tr>
                  <th>Currency</th>
                  <td>{item.currency?.toUpperCase() || "—"}</td>
                </tr>
                <tr>
                  <th>Status</th>
                  <td>
                    <StatusBadge status={item.status} />
                    {item.errorMessage && (
                      <div style={{ color: "#e74c3c", fontSize: 12, marginTop: 4 }}>
                        {item.errorCode ? `${item.errorCode}: ` : ""}{item.errorMessage}
                      </div>
                    )}
                  </td>
                </tr>
                <tr>
                  <th>Date</th>
                  <td>
                    {item.createdAt
                      ? moment(item.createdAt).format("DD MMM YYYY, HH:mm")
                      : "—"}
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Stripe references — for reconciliation against the dashboard / a payout */}
            <h6 className="fw-semibold text-muted mb-2" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}>
              Stripe references
            </h6>
            <table className="table table-bordered table-sm mb-4">
              <tbody>
                {[
                  ["Invoice", item.stripeInvoiceId],
                  ["Charge", item.stripeChargeId],
                  ["PaymentIntent", item.stripePaymentIntentId],
                  ["Subscription", item.stripeSubscriptionId],
                  ["Customer", item.stripeCustomerId],
                ].map(([label, val]) => (
                  <tr key={label as string}>
                    <th style={{ width: 170 }}>{label}</th>
                    <td>
                      {val ? (
                        <code style={{ fontSize: 11, wordBreak: "break-all" }}>{val}</code>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Copy-to-clipboard helper ─────────────────────────────────────────────────
function CopyableId({ id }: { id?: string }) {
  const [copied, setCopied] = useState(false);
  if (!id) return <span style={{ color: "#aaa" }}>—</span>;
  const short = id.length > 16 ? `${id.substring(0, 8)}…` : id;
  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(id).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
      <code style={{ fontSize: 11 }}>{short}</code>
      <button
        type="button"
        onClick={handleCopy}
        title={copied ? "Copied!" : "Copy ID"}
        style={{
          background: "none",
          border: "none",
          padding: "0 2px",
          cursor: "pointer",
          color: copied ? "#27ae60" : "#aaa",
          fontSize: 11,
        }}
      >
        <i className={`fa-solid ${copied ? "fa-check" : "fa-copy"}`} />
      </button>
    </span>
  );
}

// ─── Main screen ──────────────────────────────────────────────────────────────
const TransactionListing: React.FC = () => {
  const [countData, setCountData] = useState<number>(20);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [searchInput, setSearchInput] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [appliedFrom, setAppliedFrom] = useState<string | undefined>(undefined);
  const [appliedTo, setAppliedTo] = useState<string | undefined>(undefined);
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [selectedItem, setSelectedItem] = useState<any>(null);

  // Debounce search
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPageNumber(1);
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  const { data, isLoading, isError } = useGetTransactionLogs({
    page: pageNumber,
    limit: countData,
    search: debouncedSearch,
    fromDate: appliedFrom,
    toDate: appliedTo,
    type: typeFilter || undefined,
    status: statusFilter || undefined,
  });

  const rows: any[] = data?.data?.transactionLogs ?? [];
  const totalPages: number = data?.data?.totalPages ?? 1;
  const totalRecords: number = data?.data?.totalRecords ?? 0;
  const isEmpty = !isLoading && (rows.length === 0 || isError);

  // Range-wide totals from the API (whole filtered set, not just this page).
  const totals = data?.data?.totals ?? {
    grossCollected: 0,
    taxCollected: 0,
    refunded: 0,
    netCollected: 0,
    succeededCount: 0,
    failedCount: 0,
    refundCount: 0,
  };
  const primaryCurrency = rows.find((r) => r.currency)?.currency ?? "gbp";
  const sym = getSymbolFromCurrency(primaryCurrency) || "£";

  const handleApply = () => {
    setAppliedFrom(fromDate || undefined);
    setAppliedTo(toDate || undefined);
    setPageNumber(1);
  };

  const handleClear = () => {
    setSearchInput("");
    setDebouncedSearch("");
    setFromDate("");
    setToDate("");
    setAppliedFrom(undefined);
    setAppliedTo(undefined);
    setTypeFilter("");
    setStatusFilter("");
    setPageNumber(1);
  };

  const exportCsv = () => {
    if (!rows.length) return;
    const cols = [
      ["Date", "createdAt"],
      ["Type", "type"],
      ["Status", "status"],
      ["Owner", "name"],
      ["Email", "email"],
      ["Key", "keyName"],
      ["Currency", "currency"],
      ["Gross", "grossAmount"],
      ["Paid", "paidAmount"],
      ["Tax", "taxAmount"],
      ["Discount", "discountAmount"],
      ["Refund", "refundAmount"],
      ["Coupon", "coupon"],
      ["Invoice ID", "stripeInvoiceId"],
      ["Charge ID", "stripeChargeId"],
      ["PaymentIntent ID", "stripePaymentIntentId"],
      ["Subscription ID", "stripeSubscriptionId"],
      ["Customer ID", "stripeCustomerId"],
      ["Error", "errorMessage"],
    ];
    const esc = (v: any) => {
      let s = String(v ?? "");
      // Neutralise spreadsheet formula injection (=, +, -, @, tab, CR).
      if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
      return `"${s.replace(/"/g, '""')}"`;
    };
    const csv = [
      cols.map((c) => c[0]).join(","),
      ...rows.map((r) => cols.map((c) => esc(r[c[1]])).join(",")),
    ].join("\r\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `transactions_page${pageNumber}.csv`;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  return (
    <div className="main-wrapper">
      <Header title="Transactions" />
      <CustomLaoder loader={isLoading}>
        <div className="content-wrapper">
          <section className="main-section coach-section spacer">
            <div className="container">

              {/* ── Summary stat cards (whole filtered range) ── */}
              <div className="row g-3 mb-4">
                <StatCard
                  icon="fa-solid fa-sterling-sign"
                  label="Net collected"
                  value={`${sym}${(totals.netCollected ?? 0).toFixed(2)}`}
                  colour="#27ae60"
                />
                <StatCard
                  icon="fa-solid fa-rotate-left"
                  label={`Refunded (${totals.refundCount ?? 0})`}
                  value={`${sym}${(totals.refunded ?? 0).toFixed(2)}`}
                  colour="#8e44ad"
                />
                <StatCard
                  icon="fa-solid fa-receipt"
                  label="Tax collected"
                  value={`${sym}${(totals.taxCollected ?? 0).toFixed(2)}`}
                  colour="#f39c12"
                />
                <StatCard
                  icon="fa-solid fa-triangle-exclamation"
                  label={`Failed payments`}
                  value={String(totals.failedCount ?? 0)}
                  colour="#e74c3c"
                />
              </div>

              <div className="wrapper-card">
                {/* ── Filter bar ── */}
                <div className="row g-3 align-items-end mb-3">
                  {/* Search */}
                  <div className="col-md-4">
                    <label
                      className="form-label fw-semibold"
                      style={{ fontSize: 13 }}
                    >
                      Search
                    </label>
                    <div
                      className="form-block p-relative"
                      style={{ position: "relative" }}
                    >
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Name or email…"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        autoComplete="off"
                      />
                      <span className="search-icon" />
                      {searchInput && (
                        <button
                          type="button"
                          onClick={() => setSearchInput("")}
                          style={{
                            position: "absolute",
                            right: 10,
                            top: "50%",
                            transform: "translateY(-50%)",
                            background: "none",
                            border: "none",
                            padding: 0,
                            cursor: "pointer",
                            color: "#aaa",
                            fontSize: 14,
                          }}
                        >
                          <i className="fa-solid fa-circle-xmark" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* From date */}
                  <div className="col-md-2">
                    <label
                      className="form-label fw-semibold"
                      style={{ fontSize: 13 }}
                    >
                      From
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={fromDate}
                      max={toDate || undefined}
                      onChange={(e) => setFromDate(e.target.value)}
                    />
                  </div>

                  {/* To date */}
                  <div className="col-md-2">
                    <label
                      className="form-label fw-semibold"
                      style={{ fontSize: 13 }}
                    >
                      To
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={toDate}
                      min={fromDate || undefined}
                      onChange={(e) => setToDate(e.target.value)}
                    />
                  </div>

                  {/* Type */}
                  <div className="col-6 col-md-2">
                    <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Type</label>
                    <select
                      className="form-select"
                      value={typeFilter}
                      onChange={(e) => { setTypeFilter(e.target.value); setPageNumber(1); }}
                    >
                      <option value="">All types</option>
                      <option value="initial">Initial</option>
                      <option value="recurring">Renewal</option>
                      <option value="upgrade">Upgrade</option>
                      <option value="downgrade">Downgrade</option>
                      <option value="refund">Refund</option>
                      <option value="storage_charge">Storage charge</option>
                    </select>
                  </div>

                  {/* Status */}
                  <div className="col-6 col-md-2">
                    <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Status</label>
                    <select
                      className="form-select"
                      value={statusFilter}
                      onChange={(e) => { setStatusFilter(e.target.value); setPageNumber(1); }}
                    >
                      <option value="">All statuses</option>
                      <option value="succeeded">Succeeded</option>
                      <option value="failed">Failed</option>
                      <option value="refunded">Refunded</option>
                      <option value="partially_refunded">Partially refunded</option>
                      <option value="pending">Pending</option>
                    </select>
                  </div>

                  {/* Buttons */}
                  <div className="col-12 col-md-8 d-flex gap-2 align-items-end flex-wrap">
                    <button className="btn primary-btn" onClick={handleApply}>
                      <i className="fa-solid fa-magnifying-glass me-2" />
                      Apply
                    </button>
                    <button className="btn secondary-btn" onClick={handleClear}>
                      <i className="fa-solid fa-xmark me-2" />
                      Clear
                    </button>
                    <button
                      className="btn secondary-btn"
                      onClick={exportCsv}
                      disabled={!rows.length}
                      title="Download this page as CSV"
                    >
                      <i className="fa-solid fa-file-csv me-2" />
                      Export page
                    </button>
                  </div>
                </div>

                {/* Result count */}
                {!isLoading && !isEmpty && (
                  <p
                    className="text-muted mb-2"
                    style={{ fontSize: "0.8rem" }}
                  >
                    {totalRecords.toLocaleString()} transaction
                    {totalRecords !== 1 ? "s" : ""}
                    {debouncedSearch && ` for "${debouncedSearch}"`}
                    {appliedFrom && ` from ${moment(appliedFrom).format("DD MMM YYYY")}`}
                    {appliedTo && ` to ${moment(appliedTo).format("DD MMM YYYY")}`}
                    {" · "}succeeded {totals.succeededCount ?? 0}
                    {" · "}failed {totals.failedCount ?? 0}
                  </p>
                )}

                {/* ── Table ── */}
                {isEmpty ? (
                  <NoRecordFound />
                ) : (
                  <div className="table-blk table-responsive pb-2">
                    <table
                      id="myTable"
                      className="display border-0"
                      style={{ fontSize: 13 }}
                    >
                      <thead>
                        <tr>
                          <th>User</th>
                          <th>Key</th>
                          <th>Type</th>
                          <th>Reference (Stripe)</th>
                          <th>Gross</th>
                          <th>Paid</th>
                          <th>Tax</th>
                          <th>Refund</th>
                          <th>Status</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((item: any, i: number) => {
                          const refType = item.type === "refund";
                          const ref =
                            item.stripeInvoiceId ||
                            item.stripeChargeId ||
                            item.stripePaymentIntentId ||
                            item.stripeSubscriptionId;
                          return (
                            <tr
                              key={item._id || i}
                              style={{ cursor: "pointer", verticalAlign: "middle" }}
                              onClick={() => setSelectedItem(item)}
                              title="Click to view detail"
                            >
                              {/* Name + avatar */}
                              <td>
                                <span className="table-profile">
                                  <span className="table-profileimg">
                                    <img
                                      src={
                                        item.profileImage
                                          ? `${process.env.REACT_APP_ASSETS_URL}/uploads/profileImages/${item.profileImage}`
                                          : "assets/images/icons/dummy-user.png"
                                      }
                                      alt="user"
                                      onError={(e) => {
                                        e.currentTarget.src =
                                          "assets/images/icons/dummy-user.png";
                                      }}
                                    />
                                  </span>
                                  <span className="table-username text-capitalize">
                                    {item.name || "—"}
                                    <span style={{ display: "block", fontSize: 11, color: "#888", fontWeight: 400 }}>
                                      {item.email}
                                    </span>
                                  </span>
                                </span>
                              </td>

                              <td style={{ fontSize: 12, color: "#555", whiteSpace: "nowrap" }}>
                                {item.keyName || "—"}
                              </td>

                              <td style={{ whiteSpace: "nowrap" }}>
                                <span
                                  className="text-capitalize"
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 600,
                                    padding: "2px 7px",
                                    borderRadius: 4,
                                    background: refType ? "#f3e6f8" : "#eef6f5",
                                    color: refType ? "#8e44ad" : "#2c7a75",
                                  }}
                                >
                                  {item.type === "recurring" ? "renewal" : item.type || "—"}
                                </span>
                              </td>

                              {/* Reconciliation reference — copy */}
                              <td onClick={(e) => e.stopPropagation()}>
                                <CopyableId id={ref} />
                              </td>

                              <td style={{ whiteSpace: "nowrap" }}>
                                {sym}
                                {Number(item.grossAmount ?? 0).toFixed(2)}
                                {Number(item.discountAmount) > 0 && (
                                  <span style={{ marginLeft: 5, color: "#72BCB8", fontWeight: 600, fontSize: 11 }}>
                                    -{sym}{Number(item.discountAmount).toFixed(2)}
                                  </span>
                                )}
                              </td>

                              <td style={{ whiteSpace: "nowrap" }}>
                                <strong>{sym}{Number(item.paidAmount ?? 0).toFixed(2)}</strong>
                              </td>

                              <td style={{ whiteSpace: "nowrap" }}>
                                {sym}{Number(item.taxAmount ?? 0).toFixed(2)}
                              </td>

                              <td style={{ whiteSpace: "nowrap", color: "#8e44ad" }}>
                                {Number(item.refundAmount) > 0
                                  ? `-${sym}${Number(item.refundAmount).toFixed(2)}`
                                  : "—"}
                              </td>

                              <td>
                                <StatusBadge status={item.status} />
                                {item.errorCode && (
                                  <div style={{ fontSize: 10, color: "#e74c3c" }}>{item.errorCode}</div>
                                )}
                              </td>

                              <td style={{ whiteSpace: "nowrap", fontSize: 12, color: "#6c757d" }}>
                                {item.createdAt
                                  ? moment(item.createdAt).format("DD MMM YYYY, HH:mm")
                                  : "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>

                    <Pagination
                      totalPages={totalPages}
                      currentPage={pageNumber}
                      handlePrevPage={() =>
                        setPageNumber((p) => Math.max(1, p - 1))
                      }
                      handleNextPage={() =>
                        setPageNumber((p) =>
                          p < totalPages ? p + 1 : p
                        )
                      }
                      handleRowLimitChange={(v: number) => {
                        setCountData(v);
                        setPageNumber(1);
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>
      </CustomLaoder>

      {/* Detail modal */}
      {selectedItem && (
        <DetailModal
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
        />
      )}
    </div>
  );
};

export default TransactionListing;
